import { yupResolver } from "@hookform/resolvers/yup";
import { message } from "antd";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  createKit,
  refreshTableKits,
  selectIsUpdateKit,
  selectKitForUpdate,
  selectKitsRefreshTable,
  setIsKitForUpdate,
  setKitForUpdate,
  updateKit,
} from "../../../redux";
import { DataKits } from "../../../redux/@types";
import useAppDispatch from "../../../redux/hooks/useAppDispatch";
import { TableProperties } from "../../../shared/components/ui/Table/types";
import { validateCreateKits, validateUpdateKits } from "../../../helpers";

function normalizeFeatured(value: DataKits["featured"]) {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return Boolean(value);
}

function featuredFormValue(value: unknown) {
  return value === true || value === 1 || value === "true" ? "true" : "false";
}

export const useKits = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isUpdateKit = useSelector(selectIsUpdateKit);
  const dataUpdateKit = useSelector(selectKitForUpdate);
  const isRefreshTableKits = useSelector(selectKitsRefreshTable);

  const [isLoading, setIsLoading] = useState(false);
  const [tableProps, setTableProps] = useState<TableProperties>();

  const navigateKitsCreate = () => {
    dispatch(setIsKitForUpdate(false));
    navigate(`/kits/new`);
  };

  const formMethodsCreate = useForm<DataKits>({
    resolver: yupResolver(validateCreateKits),
  });

  const formMethodsUpdate = useForm<DataKits>({
    resolver: yupResolver(validateUpdateKits),
  });

  useEffect(() => {
    if (isUpdateKit) {
      formMethodsCreate.reset();
      formMethodsUpdate.reset({ ...dataUpdateKit });
    }

    return () => {
      formMethodsUpdate.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateKit, dataUpdateKit]);

  const updateStatus = async (value: "active" | "inactive", id: any) => {
    if (isLoading) return;
    setIsLoading(true);
    await updateKit({
      id,
      status: value,
    })
      .then((res: Partial<DataKits>) => {
        setIsLoading(false);
        if (res.id) {
          message.success(
            `Se ha actualizado con éxito el estado del kit con id:${res.id}`
          );
          dispatch(refreshTableKits(true));
        } else {
          message.error(res as string);
        }
      })
      .catch((err: any) => {
        setIsLoading(false);
        console.log(err);
        message.error(err.message);
      });
  };

  const onSubmitCreateOrUpdate = async (data: DataKits) => {
    if (isLoading) return;
    setIsLoading(true);

    const payload = {
      ...data,
      featured: normalizeFeatured(data.featured),
      price: Number(data.price),
      priority: data.priority != null ? Number(data.priority) : 0,
    };

    if (isUpdateKit) {
      await updateKit({
        id: data.id,
        name: payload.name,
        status: payload.status,
        audience: payload.audience,
        clase: payload.clase,
        eyebrow: payload.eyebrow,
        price: payload.price,
        description: payload.description,
        image: payload.image,
        ideal_for: payload.ideal_for,
        featured: payload.featured,
        priority: payload.priority,
      })
        .then((res: Partial<DataKits>) => {
          setIsLoading(false);
          if (res.id) {
            message.success(
              `Se ha actualizado con éxito el kit con id:${res.id}`
            );
            dispatch(refreshTableKits(true));
          } else {
            message.error(res as string);
          }
        })
        .catch((err: any) => {
          setIsLoading(false);
          console.log(err);
          message.error(err.message);
        });
    } else {
      await createKit(payload)
        .then((res: DataKits) => {
          setIsLoading(false);
          if (res.id) {
            message.success(`Se ha creado con éxito el kit con id:${res.id}`);
            dispatch(refreshTableKits(true));
            dispatch(
              setKitForUpdate({
                ...res,
                featured: featuredFormValue(res.featured),
              })
            );
            dispatch(setIsKitForUpdate(true));
            // Ir al kit creado y abrir pestaña de productos sin recargar.
            navigate(`/kits/${res.id}`, { replace: true, state: { kitTab: "2" } });
            window.scrollTo(0, 0);
          } else {
            message.error(res as any);
          }
        })
        .catch((err: any) => {
          setIsLoading(false);
          console.log(err);
          message.error(err.message);
        });
    }
  };

  useEffect(() => {
    if (isRefreshTableKits) {
      tableProps?.refresh();
      dispatch(refreshTableKits(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshTableKits]);

  return {
    isUpdateKit,
    isLoading,
    tableProps,
    formMethodsCreate,
    formMethodsUpdate,
    setTableProps,
    onSubmitCreateOrUpdate,
    navigateKitsCreate,
    updateStatus,
  };
};
